import type { Mesh, MeshStandardMaterial } from 'three';

import { createPlainMaterial } from '../../../builders/geometry-builder/helpers';

/**
 * Apply a per-face surround material array to a mesh whose geometry still carries the cap split.
 *
 * `three-csg-ts` carries every polygon's materialIndex through both union and subtract, so a panel
 * split by `splitExtrudeCaps` still knows which faces are the outward finish and which are the plain
 * inner cap, sides and cut walls. A mesh with no group for the finish index -- a slat `csgModel`
 * panel, a legacy single-material panel -- is left untouched so the caller can apply whatever
 * fallback it used before the split existed. The two callers do NOT share one: the merged mesh falls
 * back to `classifySurroundFaces`, while a finish change assigns the single material it always did.
 *
 * @param mesh      The panel mesh; its material is assigned in place only when the groups are present
 * @param materials Material array in geometry-group order, finish material last
 * @returns true when the groups were present and the array was applied
 */
export const applySurroundMaterials = (mesh: Mesh, materials: Array<MeshStandardMaterial>): boolean => {
	const { groups } = mesh.geometry;
	const texturedIndex = materials.length - 1;
	const hasOutwardGroup = groups.some(({ materialIndex, count }) => materialIndex === texturedIndex && count > 0);

	if (texturedIndex < 1 || !hasOutwardGroup) {
		return false;
	}

	const applied = [...materials];
	// CSG appends a trailing group for polygons that carried no index of their own
	const highestIndex = groups.reduce((highest, { materialIndex }) => Math.max(highest, materialIndex ?? 0), 0);
	while (applied.length <= highestIndex) {
		applied.push(createPlainMaterial(materials[texturedIndex]));
	}

	mesh.material = applied;
	return true;
};

/**
 * Release the plain materials built for a call to `applySurroundMaterials` that did not take the
 * group path, so a fallback that needs only the finish does not leak them.
 *
 * @param materials The array that was offered, finish material last; every entry before it is freed
 */
export const disposeUnusedSurroundMaterials = (materials: Array<MeshStandardMaterial>): void => {
	materials.slice(0, -1).forEach((unused) => unused.dispose());
};
